import React from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, useTheme, useMediaQuery } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column } from './DataTable';
interface ReversalRecord {
  id: string;
  transactionId: string;
  mtoName: string;
  beneficiaryName: string;
  amount: string;
  currency: string;
  reversalReason: string;
  status: 'PENDING' | 'PROCESSED' | 'FAILED';
  processedAt?: string;
}
interface ReversalRecordsDrawerProps {
  open: boolean;
  onClose: () => void;
  uploadId: string;
  fileName: string;
  records: ReversalRecord[];
}
const ReversalRecordsDrawer: React.FC<ReversalRecordsDrawerProps> = ({
  open,
  onClose,
  uploadId,
  fileName,
  records
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const columns: Column<ReversalRecord>[] = [{
    id: 'id',
    label: 'Record ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-2093" data-mp="2fd8dkvyiiofj">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-2094" data-mp="8avdsp3tba1m">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-2095" data-mp="3pseg9u99as92">
          {row.mtoName}
        </Typography>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" noWrap data-id="element-2096" data-mp="3gt9kgvp2xd0l">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'amount',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-2097" data-mp="36utren6hrtog">
          {row.currency} {row.amount}
        </Typography>
  }, {
    id: 'reversalReason',
    label: 'Reversal Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-2098" data-mp="326koh3u1dhyy">
          {row.reversalReason}
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      const colors = {
        PENDING: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        PROCESSED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[row.status];
      return <Chip label={row.status} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2099" data-mp="2ob79se0bspv" />;
    }
  }, {
    id: 'processedAt',
    label: 'Processed At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap data-id="element-2100" data-mp="21wjm0lzrhaqs">
          {row.processedAt ? new Date(row.processedAt).toLocaleString() : '-'}
        </Typography>
  }];
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : '90%',
      maxWidth: 1200
    }
  }} data-id="element-2101" data-mp="1996zgbtlp2xh">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-2102" data-mp="1j869nyu0l6ml">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }} data-id="element-2103" data-mp="2uli81u708x1d">
          <Box data-id="element-2104" data-mp="z638za99qjx8">
            <Typography variant="h6" fontWeight={600} data-id="element-2105" data-mp="z6leyylqgcic">
              Reversal Records
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
            mt: 0.5
          }} data-id="element-2106" data-mp="145funroelky8">
              Upload ID: {uploadId}
            </Typography>
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1
          }} data-id="element-2107" data-mp="1d38bj09rx0nq">
              <Icon icon="lucide:file-spreadsheet" width={16} height={16} color="#10B981" data-id="element-2108" data-mp="1n1xs8ppxof2e" />
              <Typography variant="body2" color="text.secondary" data-id="element-2109" data-mp="1vurdr4vz4aop">
                {fileName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-2110" data-mp="20t2mfcaj8f5c">
            <Icon icon="lucide:x" width={20} data-id="element-2111" data-mp="25v1p0jyg7818" />
          </IconButton>
        </Box>
        <Divider data-id="element-2112" data-mp="2fy731j21lrgk" />

        {/* Records Table */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3
      }} data-id="element-2113" data-mp="2fvuayd2km0ps">
          <DataTable title={`Reversal Records (${records.length})`} data={records} columns={columns} keyField="id" emptyStateMessage="No reversal records found" initialVisibleColumns={['id', 'transactionId', 'mtoName', 'beneficiaryName', 'amount', 'reversalReason', 'status', 'processedAt']} data-id="element-2114" data-mp="25zrdvlmkss9p" />
        </Box>
      </Box>
    </Drawer>;
};
export default ReversalRecordsDrawer;